import { useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import { Navigation } from '../Navigation'
import { ErpInfo } from 'app/components/Erp/ErpInfo'
import { accountingSystemOptions } from 'utils/constants/accountingSystems'
import { FormCard } from 'shared/formCard.interface'
import { OrganizationOption } from 'shared/organizationResponse.interface'
import { AccountingSystemOption } from 'shared/accountingSystem.interface'
import { FormErrorLabel } from 'app/components/Labels/FormErrorLabel'
import { getOrganizations } from 'utils/organizations'
import { createOtherAccountingSystemOption } from 'utils'
import { SubsidiaryCheckbox } from 'app/components/Checkboxes/SubsidiaryCheckbox'
import { emailRegex } from 'shared/onboardingDetails.validator'
import { Country, countryOptions } from 'utils/constants/countries'
import { CountryOption } from 'shared/country.interface'
import { CountrySelector } from 'app/components/Input/CountrySelector';

type FormData = {
  hasSubsidiaries: boolean
  organization: OrganizationOption
  country: CountryOption
  subsidiaries: OrganizationOption[]
  accountingSystem: AccountingSystemOption
  otherAccountingSystem: string
  sdworxContact: string
}

export const CompanyCard = ({
  form,
  setForm,
  currentPage,
  setCurrentPage,
  disableNavigation,
  onSummaryPage,
}: FormCard) => {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormData>()

  useEffect(() => {
    form.hasSubsidiaries && setValue('hasSubsidiaries', form.hasSubsidiaries)
    form.country && setValue('country', form.country)
    form.subsidiaries && setValue('subsidiaries', form.subsidiaries)
    form.organization && setValue('organization', form.organization)
    form.accountingSystem && setValue('accountingSystem', form.accountingSystem)
    form.sdworxContact && setValue('sdworxContact', form.sdworxContact)
  }, [form.subsidiaries, form.country, form.organization, form.accountingSystem, form.sdworxContact, form.hasSubsidiaries, setValue])

  useEffect(() => {
    const subscription = watch(value => {
      setForm({
        ...form,
        hasSubsidiaries: value.hasSubsidiaries ?? false,
        country: value.country as CountryOption,
        organization: value.organization as OrganizationOption,
        subsidiaries: value.hasSubsidiaries && value.country?.value === form.country?.value ? (value.subsidiaries as OrganizationOption[]) : [],
        accountingSystem:
          value.accountingSystem?.value?.id === 'annet'
            ? createOtherAccountingSystemOption(value.otherAccountingSystem)
            : (value.accountingSystem as AccountingSystemOption),
        sdworxContact: value.sdworxContact as string,
      })
      //* Trigger input validation when on summary page, needs a small timeout to wait until otherAccountingSystem field is registered
      onSummaryPage && setTimeout(() => trigger(), 1)
    })
    return () => subscription.unsubscribe()
  }, [watch, form, setForm, onSummaryPage, trigger])

  const onSubmit = () => setCurrentPage(currentPage + 1)

  return (
    <div className="d-flex justify-content-center align-items-center">
      <form onSubmit={handleSubmit(onSubmit)} className="w-100 w-md-50">
        <div className="mb-3">
          <SubsidiaryCheckbox register={register} />
        </div>
        <CountrySelector control={control} errors={errors} countryOptions={countryOptions} />
        <div className="mb-3">
          <label
            id="select-organization-label"
            htmlFor="select-organization"
            className="form-label"
          >
            {form.hasSubsidiaries ? 'Morselskapets organisasjonsnummer ' : 'Organisasjonsnummer'}
          </label>
          <Controller
            control={control}
            name="organization"
            rules={{ required: 'Vennligst velg ditt selskap.' }}
            render={({ field }) => (
              form.country?.value === Country.Norge ? (
              <AsyncSelect
                {...field}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    padding: '3.5px 0',
                    backgroundColor: 'transparent',
                    borderRadius: '0',
                    borderColor: state.isFocused ? '#45095a' : '#ced4da',
                    boxShadow: 'none',
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: state.isFocused ? '#fb8caf' : '#fff',
                    color: '#45095a',
                  }),
                }}
                classNamePrefix="react-select"
                aria-labelledby="select-organization-label"
                inputId="select-organization"
                placeholder="Søk og velg ditt selskap"
                noOptionsMessage={input =>
                  input.inputValue.length < 2
                    ? 'Søk etter ditt selskap..'
                    : 'Vi kunne ikke finne noen resultater for ditt søk'
                }
                cacheOptions
                loadOptions={getOrganizations}
                loadingMessage={_ => 'Søker..'}
                defaultOptions
                defaultValue={form.organization}
              />
              ) : (
                <>
                  <input
                    aria-labelledby="organization-number-label"
                    id="organization-number"
                    type="text"
                    className="form-control"
                    placeholder="Skriv inn ditt organisasjonsnummer"
                    {...register('organization.value.organizationNumber' as 'organization.value.organizationNumber', {
                      required: 'Vennligst fyll inn organisasjonsnummer.',
                      })
                    }
                    defaultValue={form.organization?.value?.organizationNumber}
                  />
                  <FormErrorLabel label={errors.organization?.value?.organizationNumber?.message} />
                  <label
                    id="organization-name-label"
                    htmlFor="organization-name"
                    className="form-label mt-3"
                  >
                    {form.hasSubsidiaries ? 'Morselskapets navn ' : 'Selskapsnavn'}
                  </label>
                  <input
                    aria-labelledby="organization-name-label"
                    id="organization-name"
                    type="text"
                    className="form-control"
                    placeholder="Skriv inn ditt selskapsnavn"
                    {...register('organization.value.name' as 'organization.value.name', {
                      required: 'Vennligst fyll inn selskapsnavn.',
                    })
                    }
                    defaultValue={form.organization?.value?.name}
                  />
                  <FormErrorLabel label={errors.organization?.value?.name?.message} />
                </>
              ))
            }
          />
          <FormErrorLabel label={errors.organization?.message} />
        </div>
        {form.hasSubsidiaries && (
          <div className="mb-3">
            <label
              id="select-subsidiaries-label"
              htmlFor="select-subsidiaries"
              className="form-label"
            >
              Datterselskaper
            </label>
            <Controller
              control={control}
              name="subsidiaries"
              rules={{ required: 'Vennligst velg minst et eller flere datterselskap.' }}
              render={({ field }) => (
                form.country?.value === Country.Norge ? (
                <AsyncSelect
                  {...field}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      padding: '3.5px 0',
                      backgroundColor: 'transparent',
                      borderRadius: '0',
                      borderColor: state.isFocused ? '#45095a' : '#ced4da',
                      boxShadow: 'none',
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      backgroundColor: state.isFocused ? '#fb8caf' : '#fff',
                      color: '#45095a',
                    }),
                    multiValue: baseStyles => ({
                      ...baseStyles,
                      backgroundColor: '#fb8caf',
                      color: '#45095a',
                    }),
                    multiValueLabel: baseStyles => ({
                      ...baseStyles,
                      backgroundColor: '#fb8caf',
                      color: '#45095a',
                    }),
                  }}
                  classNamePrefix="react-select"
                  aria-labelledby="select-subsidiaries-label"
                  inputId="select-subsidiaries"
                  placeholder="Søk og velg datterselskaper"
                  noOptionsMessage={input =>
                    input.inputValue.length < 2
                      ? 'Søk etter datterselskap..'
                      : 'Vi kunne ikke finne noen resultater for ditt søk'
                  }
                  cacheOptions
                  loadOptions={getOrganizations}
                  loadingMessage={_ => 'Søker..'}
                  defaultOptions
                  defaultValue={form.subsidiaries}
                  isMulti
                />) :
                (
                  <div className="d-flex flex-column">
                    {form.subsidiaries?.map((subsidiary, index) => (
                      <div key={index} className="d-flex mb-2">
                        <div className="form-label mt-1 me-2 flex-grow-1">
                        <input
                          aria-labelledby="subsidiary-number-label"
                          id="subsidiary-number"
                          type="text"
                          className="form-control me-2"
                          placeholder="Organisasjonsnummer"
                          {...register(`subsidiaries.${index}.value.organizationNumber` as `subsidiaries.${number}.value.organizationNumber`, {
                            required: 'Vennligst fyll inn organisasjonsnummer.',
                          })}
                          defaultValue={subsidiary.value.organizationNumber}
                        />
                        <FormErrorLabel label={errors.subsidiaries?.[index]?.value?.organizationNumber?.message} />
                        </div>
                        <div className="form-label mt-1 me-2 flex-grow-1">
                        <input
                          aria-labelledby="subsidiary-name-label"
                          id="subsidiary-name"
                          type="text"
                          className="form-control"
                          placeholder="Navn"
                          {...register(`subsidiaries.${index}.value.name` as `subsidiaries.${number}.value.name`, {
                            required: 'Vennligst fyll inn selskapsnavn.',
                          })}
                          defaultValue={subsidiary.value.name}
                        />
                          <FormErrorLabel label={errors.subsidiaries?.[index]?.value?.name?.message} />
                        </div>
                        {form.subsidiaries.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-danger mt-1 mb-2"
                          onClick={() => {
                            const updatedSubsidiaries = [...(field.value || [])]
                            updatedSubsidiaries.splice(index, 1)
                            setValue('subsidiaries', updatedSubsidiaries)
                          }}
                        >
                          &times;
                        </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        const newSubsidiary = {
                          value: {
                            organizationNumber: '',
                            name: '',
                          },
                        }
                        setValue('subsidiaries', [...(field.value || []), newSubsidiary as OrganizationOption])
                      }}
                    >
                      Legg til datterselskap
                    </button>
                  </div>
                )
              )}
            />
            <FormErrorLabel label={errors.subsidiaries?.message} />
          </div>
        )}
        <div className="mb-3">
          <label
            id="select-accounting-system-label"
            htmlFor="select-accounting-system"
            className="form-label"
          >
            Regnskapssystem
          </label>
          <Controller
            control={control}
            name="accountingSystem"
            rules={{ required: 'Vennligst velg ditt regnskapssystem.' }}
            render={({ field }) => (
              <Select
                {...field}
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    padding: '3.5px 0',
                    backgroundColor: 'transparent',
                    borderRadius: '0',
                    borderColor: state.isFocused ? '#45095a' : '#ced4da',
                    boxShadow: 'none',
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: state.isFocused ? '#fb8caf' : '#fff',
                    color: '#45095a',
                  }),
                }}
                classNamePrefix="react-select"
                aria-labelledby="select-accounting-system-label"
                inputId="select-accounting-system"
                options={accountingSystemOptions}
                placeholder="Velg ditt regnskapssystem"
                noOptionsMessage={_ => 'Velg Annet om du ikke finner ditt regnskapssystem'}
                isClearable
              />
            )}
          />
          <FormErrorLabel label={errors.accountingSystem?.message} />
        </div>
        {form.accountingSystem?.value.id === 'sdworx' && (
          <div className="mb-3">
            <label
              id="sdworx-contact-label"
              htmlFor="sdworx-contact"
              className="form-label"
            >
              E-postadresse til kontaktperson i SD Worx
            </label>
            <input
              aria-labelledby="sdworx-contact-label"
              id="sdworx-contact"
              type="email"
              className="form-control"
              placeholder="ola.nordmann@gmail.com"
              defaultValue={form.sdworxContact ?? ''}
              {...register('sdworxContact', {
                required: 'Vennligst fyll inn e-postadresse til din kontaktperson i SD Worx.',
                pattern: {
                  value: emailRegex,
                  message: 'Vennligst skriv inn en gyldig e-post adresse.',
                },
              })}
            />
            <FormErrorLabel label={errors.sdworxContact?.message} />
          </div>
        )
        }
        {form.accountingSystem?.value.id === 'annet' && (
          <div className="mb-3">
            <label
              id="other-accounting-system-label"
              htmlFor="other-accounting-system"
              className="form-label"
            >
              Hvilket regnskapssystem har du idag?
            </label>
            <input
              aria-labelledby="other-accounting-system-label"
              id="other-accounting-system"
              type="text"
              className="form-control"
              placeholder="Ditt nåværende regnskapssystem"
              defaultValue={
                form.accountingSystem?.value.name === 'Annet'
                  ? ''
                  : form.accountingSystem?.value.name
              }
              {...register('otherAccountingSystem', {
                required: 'Vennligst fyll ut ditt regnskapssystem.',
              })}
            />
            <FormErrorLabel label={errors.otherAccountingSystem?.message} />
          </div>
        )}
        {!onSummaryPage && <ErpInfo accountingSystem={form.accountingSystem} />}
        {!disableNavigation && (
          <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} />
        )}
      </form>
    </div>
  )
}
